import { IFreePlayVouchersProvider } from "../free-play-vouchers.provider.interface";
import { IFreePlayVoucherModel } from "../../vouchers/free-play-voucher.model.interface";
import { observable, runInAction, makeObservable } from "mobx";
import { IServiceFactoryExtended } from "../../../service-factory-extended.interface";
import { VouchersProviderBase } from "../vouchers-provider-base";
import { GNWP_SOURCES } from "../../../gnwp/gnwp-consts";
import { Check } from "@sparkware/app-utils";
import { ThirdPartyFreePlayController_GetAllVouchersResponse } from "../../../api/api.service";
import { FP_VOUCHER_TYPES } from "../../../../../common/const/free_play_consts";
import { FreePlayStatus } from "../../free-play.service.interface";
import { NullableUndefinedDate, NullableUndefinedString } from "../../../../../common/types/nullable-types";
import {FreePlayVoucherData} from "../../../firebase/topics/free-play/published-data";

enum THIRD_PARTY_BONUS_TYPE {
    FreePlay = 1,
    Voucher = 2
}

type BackendBonusData = ThirdPartyFreePlayController_GetAllVouchersResponse['FreePlayBonusList'][number];

export class ThirdPartyVouchersProvider extends VouchersProviderBase implements IFreePlayVouchersProvider {
    constructor(services: IServiceFactoryExtended) {
        super(services);

        makeObservable(this);

        this.refreshVouchers();

        this.addSdkEventSubscription(this.services.sdk.events.onGameClose(async () => {
            await this.refreshVouchers();
        }));

        this.addSdkEventSubscription(this.services.sdk.events.onExternalLinkClosed(async (source) => {
            if (source === GNWP_SOURCES.CASHIER || source === GNWP_SOURCES.MY_ACCOUNT || source === GNWP_SOURCES.OMG) {
                await this.refreshVouchers();
            }
        }));

        this.addSdkEventSubscription(this.services.sdk.events.onDepositWidgetClosed(async () => {
            await this.refreshVouchers();
        }));

        this.addSdkEventSubscription(this.services.sdk.events.onFreeToPlayWidgetClosed(async () => {
            await this.refreshVouchers();
        }));
    }

    @observable
    vouchers: IFreePlayVoucherModel[] = [];

    private _setVouchers(newVouchers: IFreePlayVoucherModel[]): void {
        runInAction(() => {
            this.vouchers = newVouchers;
        });
    }

    dispose() {
        super.dispose();

        this.vouchers.forEach(voucher => voucher.dispose());
        this.vouchers = [];
    }

    async refreshVouchers(): Promise<void> {
        const response = await this.services.api.thirdPartyFreePlayController.getAllVouchers();
        if (Check.isEmpty(response.FreePlayBonusList)) {
            this._setVouchers([]);
            return;
        }

        const voucherModels: IFreePlayVoucherModel[] = [];

        for (let bonusDataFromBackend of response.FreePlayBonusList) {
            if (bonusDataFromBackend.BonusStatus === FreePlayStatus.Active) {
                const clientVoucherData = await this._convertBackendBonusDataToClientVoucherData(bonusDataFromBackend);
                if (clientVoucherData) {
                    const voucherModel = this._createVoucherModel(clientVoucherData);
                    if (voucherModel) {
                        voucherModels.push(voucherModel);
                    }
                }
            }

        }

        this._setVouchers(voucherModels);

    }

    private async _convertBackendBonusDataToClientVoucherData(bonusDataFromBackend: BackendBonusData): Promise<FreePlayVoucherData | null> {
        if (Check.isEmpty(bonusDataFromBackend.CurrencyCode)) {
            this.services.logger.error(`Third party voucher with ID ${bonusDataFromBackend.FreePlayID} is missing currency code`);
            return null;
        }

        if (bonusDataFromBackend.CurrencyCode !== this.services.currency.currencyIso3) {
            this.services.logger.error(`Third party voucher with ID ${bonusDataFromBackend.FreePlayID} has currency code ${bonusDataFromBackend.CurrencyCode} which is different from user currency code ${this.services.currency.currencyIso3}`);
            return null;
        }

        switch (bonusDataFromBackend.FreePlayTypeID) {
            case THIRD_PARTY_BONUS_TYPE.FreePlay:
                return this._convertFreePlay(bonusDataFromBackend);
            case THIRD_PARTY_BONUS_TYPE.Voucher:
                return this._convertVoucher(bonusDataFromBackend);
            default:
                this.services.logger.error(`Unknown third party free play type ${bonusDataFromBackend.FreePlayTypeID}`);
                return null;
        }
    }

    private async _getGamesMaxBets(gameTypes: number[]): Promise<number[]> {
        const gamesInfo = await this.services.games.getUnrestrictedGamesInfoArray(gameTypes);

        return gamesInfo.filter(gInfo => !Check.isNullOrUndefined(gInfo?.maxBet))
            .map(gInfo => gInfo.maxBet);
    }


    private _parseVoucherCreationDate(creationDate: NullableUndefinedString): NullableUndefinedDate {
        if (creationDate) {
            return new Date(creationDate);
        } else {
            return null;
        }
    }

    private async _convertFreePlay(bonusDataFromBackend: BackendBonusData): Promise<FreePlayVoucherData | null> {
        if (!bonusDataFromBackend.FreeSpinsInfo) {
            this.services.logger.error(`Missing FreeSpinsInfo for third party voucher with ID ${bonusDataFromBackend.FreePlayID}`);
            return null;
        }

        let spinBetAmount = bonusDataFromBackend.FreeSpinsInfo.BetPerSpin;
        if (!spinBetAmount) {
            const maxBets = await this._getGamesMaxBets(bonusDataFromBackend.GameTypesIDs);
            if (maxBets.length === 0) {
                spinBetAmount = 0;
            } else {
                spinBetAmount = Math.max(...maxBets);
            }
        }
        return {
            ID: bonusDataFromBackend.FreePlayID,
            CurrentBalance: bonusDataFromBackend.FreeSpinsInfo.NumberOfFreeSpinsLeft * spinBetAmount,
            Winnings: bonusDataFromBackend.CurrentWinningAmount,
            FreePlayTypeId: FP_VOUCHER_TYPES.FreeSpin,
            OriginAmount: bonusDataFromBackend.FreeSpinsInfo.NumberOfAwardedFreeSpins * spinBetAmount,
            CreationDate: this._parseVoucherCreationDate(bonusDataFromBackend.CreationDate),
            ExpirationDate: new Date(bonusDataFromBackend.ExpirationDate),
            SpinBetAmount: spinBetAmount,
            GameTypeIDList: bonusDataFromBackend.GameTypesIDs,
            CompanyID: bonusDataFromBackend.CompanyID
        };
    }

    private async _convertVoucher(bonusDataFromBackend: BackendBonusData): Promise<FreePlayVoucherData | null> {
        if (!bonusDataFromBackend.VoucherInfo) {
            this.services.logger.error(`Missing VoucherInfo for third party voucher with ID ${bonusDataFromBackend.FreePlayID}`);
            return null;
        }

        return {
            ID: bonusDataFromBackend.FreePlayID,
            CurrentBalance: bonusDataFromBackend.VoucherInfo.RemainingFreePlayAmount,
            Winnings: bonusDataFromBackend.CurrentWinningAmount,
            FreePlayTypeId: FP_VOUCHER_TYPES.FreePlay,
            OriginAmount: bonusDataFromBackend.AwardedFreePlayAmount,
            CreationDate: this._parseVoucherCreationDate(bonusDataFromBackend.CreationDate),
            ExpirationDate: new Date(bonusDataFromBackend.ExpirationDate),
            SpinBetAmount: 0,
            GameTypeIDList: bonusDataFromBackend.GameTypesIDs,
            CompanyID: bonusDataFromBackend.CompanyID
        };

    }
}